import React from 'react';
import clsx from 'clsx';
import { Icon } from './Icon';

export type ButtonVariant =
  | 'primary'
  | 'outline'
  | 'tertiary'
  | 'ghost'
  | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  iconRight?: string;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'tw-bg-ds-primary tw-text-white tw-border-transparent hover:tw-bg-ds-primary-hover active:tw-bg-ds-primary-active',
  outline:
    'tw-bg-transparent tw-text-ds-primary tw-border-ds-primary hover:tw-bg-[rgba(0,68,157,0.05)]',
  tertiary:
    'tw-bg-ds-sky-mist tw-text-ds-primary tw-border-ds-sky-mist-border hover:tw-bg-ds-sky-mist-border',
  ghost:
    'tw-bg-transparent tw-text-ds-ink-soft tw-border-ds-hairline hover:tw-bg-ds-table-header',
  danger:
    'tw-bg-ds-danger tw-text-white tw-border-transparent hover:tw-bg-ds-danger-hover',
};

export const Button = ({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) => {
  const iconOnly = !!icon && !children;
  const sizing = clsx(
    size === 'sm' ? 'tw-text-[13px]' : 'tw-text-[14px]',
    size === 'sm'
      ? iconOnly
        ? 'tw-p-[7px]'
        : 'tw-px-[14px] tw-py-[7px]'
      : iconOnly
        ? 'tw-p-[10px]'
        : 'tw-px-[20px] tw-py-[10px]'
  );
  const iconSize = size === 'sm' ? 16 : 18;
  return (
    <button
      {...rest}
      type={rest.type ?? 'button'}
      disabled={disabled}
      className={clsx(
        'tw-font-poppins tw-font-medium tw-inline-flex tw-items-center tw-gap-[8px]',
        // tw-border-solid is required: a global legacy `button { border: none }` reset
        // sets border-style:none and outranks preflight's *{border-style:solid}, so the
        // border-width alone wouldn't paint. The class restores solid style on <button>.
        'tw-rounded-full tw-border tw-border-solid tw-leading-none tw-whitespace-nowrap tw-transition-colors',
        sizing,
        disabled
          ? 'tw-bg-ds-field-disabled tw-text-ds-ink-mute tw-border-transparent tw-cursor-not-allowed'
          : clsx('tw-cursor-pointer', VARIANTS[variant]),
        className
      )}
    >
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
      {iconRight && <Icon name={iconRight} size={iconSize} />}
    </button>
  );
};
